import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Layout from "../components/layout/Layout";
import { useAuth, useDestination, API } from "../App";
import { Button } from "../components/ui/button";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";
import { formatMoney } from "../lib/safeData";
import {
  ShoppingCart,
  Trash2,
  Plus,
  Minus,
  Package,
  ArrowRight,
  Truck,
  Zap,
} from "lucide-react";
import LuxuryProductImage from "../components/LuxuryProductImage";
import ConciergeCartItem, {
  isConciergeCartItem,
  mapCartLineToConciergeItem,
} from "../components/ConciergeCartItem";
import DualLandedPrice from "../components/DualLandedPrice";
import DynamicCheckoutPriceCard from "../components/DynamicCheckoutPriceCard";
import { useGlobalCartPricing } from "../hooks/useGlobalCartPricing";

export default function CartPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { destination, currency, shippingSpeed, setShippingSpeed } = useDestination();

  const [cart, setCart] = useState(null);
  const pricedCart = useGlobalCartPricing(cart, destination);
  const [loading, setLoading] = useState(true);

  const fetchCart = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/cart`, {
        params: { destination, currency, shipping_speed: shippingSpeed },
        withCredentials: true,
      });
      setCart(response.data);
    } catch (error) {
      console.error("Error fetching cart:", error);
      toast.error("Failed to load cart");
    } finally {
      setLoading(false);
    }
  }, [destination, currency, shippingSpeed]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const updateQuantity = async (productId, quantity) => {
    try {
      if (quantity <= 0) {
        await axios.delete(`${API}/cart/remove/${productId}`, {
          withCredentials: true,
        });
      } else {
        await axios.put(
          `${API}/cart/update`,
          { product_id: productId, quantity },
          { withCredentials: true }
        );
      }
      fetchCart();
    } catch (error) {
      console.error("Error updating cart:", error);
      toast.error("Failed to update cart");
    }
  };

  const removeItem = async (productId) => {
    try {
      await axios.delete(`${API}/cart/remove/${productId}`, {
        withCredentials: true,
      });
      toast.success("Item removed");
      fetchCart();
    } catch (error) {
      console.error("Error removing item:", error);
      toast.error("Failed to remove item");
    }
  };

  const formatPrice = (price) => formatMoney(price, currency);

  const handleCheckout = () => {
    if (!user) {
      toast.info("Please sign in to checkout");
      navigate("/login", { state: { from: { pathname: "/checkout" } } });
      return;
    }
    navigate("/checkout");
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Skeleton className="h-8 w-48 mb-8" />
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-32 w-full" />
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-3xl font-bold text-slate-900 mb-8" data-testid="cart-title">
          <ShoppingCart className="inline h-8 w-8 mr-3" />
          Shopping Cart
        </h1>

        {pricedCart?.items?.length > 0 ? (
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4">
              {pricedCart.items.map((item) =>
                isConciergeCartItem(item) ? (
                  <ConciergeCartItem
                    key={item.product_id}
                    item={mapCartLineToConciergeItem(item)}
                    onRemove={() => removeItem(item.product_id)}
                    quantity={item.quantity}
                    onQuantityChange={(qty) => updateQuantity(item.product_id, qty)}
                    itemTotal={item.item_total}
                    formatPrice={formatPrice}
                    testId={`cart-item-${item.product_id}`}
                  />
                ) : (
                <div
                  key={item.product_id}
                  className="container-card p-4 flex gap-4"
                  data-testid={`cart-item-${item.product_id}`}
                >
                  {/* Image */}
                  <div className="w-24 h-24 bg-slate-100 rounded-sm overflow-hidden flex-shrink-0">
                    <LuxuryProductImage
                      product={item}
                      alt={item.name}
                      className="w-full h-full object-cover"
                      showBadge={false}
                    />
                  </div>

                  {/* Details */}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-slate-900 truncate">
                      {item.name}
                    </h3>
                    <p className="text-xs text-slate-500 mb-2">
                      {item.marketplace}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center border border-slate-200 rounded-sm">
                        <button
                          onClick={() =>
                            updateQuantity(item.product_id, item.quantity - 1)
                          }
                          className="p-2 hover:bg-slate-50"
                          data-testid={`quantity-minus-${item.product_id}`}
                        >
                          <Minus className="h-4 w-4" />
                        </button>
                        <span className="px-3 py-1 border-x border-slate-200 text-sm">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() =>
                            updateQuantity(item.product_id, item.quantity + 1)
                          }
                          className="p-2 hover:bg-slate-50"
                          data-testid={`quantity-plus-${item.product_id}`}
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                      </div>
                      <button
                        onClick={() => removeItem(item.product_id)}
                        className="p-2 text-slate-400 hover:text-red-500"
                        data-testid={`remove-${item.product_id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {/* Price */}
                  <div className="text-right">
                    <p className="text-lg font-bold text-emerald-600">
                      {formatPrice(item.item_total)}
                    </p>
                    <p className="text-xs text-slate-500">
                      {formatPrice(item.landed_price)} each
                    </p>
                  </div>
                </div>
                )
              )}
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="container-card p-6 sticky top-24">
                <h2 className="text-lg font-bold text-slate-900 mb-4">
                  Order Summary
                </h2>

                {/* Shipping Speed */}
                <div className="mb-4">
                  <p className="text-sm font-medium text-slate-700 mb-2">
                    Shipping Speed
                  </p>
                  <RadioGroup
                    value={shippingSpeed}
                    onValueChange={setShippingSpeed}
                    className="space-y-2"
                  >
                    <div
                      className={`flex items-center gap-2 p-2 rounded-sm border cursor-pointer ${
                        shippingSpeed === "standard"
                          ? "border-orange-500 bg-orange-50"
                          : "border-slate-200"
                      }`}
                      onClick={() => setShippingSpeed("standard")}
                    >
                      <RadioGroupItem value="standard" id="cart-standard" />
                      <Label htmlFor="cart-standard" className="flex items-center gap-2 cursor-pointer text-sm">
                        <Truck className="h-4 w-4" />
                        Standard (7-14 days)
                      </Label>
                    </div>
                    <div
                      className={`flex items-center gap-2 p-2 rounded-sm border cursor-pointer ${
                        shippingSpeed === "express"
                          ? "border-orange-500 bg-orange-50"
                          : "border-slate-200"
                      }`}
                      onClick={() => setShippingSpeed("express")}
                    >
                      <RadioGroupItem value="express" id="cart-express" />
                      <Label htmlFor="cart-express" className="flex items-center gap-2 cursor-pointer text-sm">
                        <Zap className="h-4 w-4" />
                        Express (3-5 days)
                      </Label>
                    </div>
                  </RadioGroup>
                </div>

                <div className="border-t border-slate-200 pt-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Destination</span>
                    <span className="font-medium">{destination}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Items ({pricedCart.items.length})</span>
                    <span>{formatPrice(pricedCart.total)}</span>
                  </div>
                  <div className="flex justify-between text-sm text-slate-500">
                    <span>Landed & Legal (Pre-paid)</span>
                    <span>Included</span>
                  </div>
                </div>

                <div className="border-t border-slate-200 mt-4 pt-4">
                  <div className="flex justify-between text-lg font-bold items-baseline gap-2">
                    <span>Total</span>
                    <span className="text-emerald-600 text-right" data-testid="cart-total">
                      {destination === "BB" ? (
                        <DualLandedPrice amount={pricedCart.total} size="md" usdClassName="text-emerald-600" />
                      ) : (
                        formatPrice(pricedCart.total)
                      )}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    All-inclusive landed price in USD with BBD at official 2:1 peg • {destination}
                  </p>
                </div>

                <Button
                  className="w-full mt-6 bg-orange-600 hover:bg-orange-700 rounded-sm"
                  size="lg"
                  onClick={handleCheckout}
                  data-testid="checkout-button"
                >
                  Proceed to Checkout
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-16">
            <ShoppingCart className="h-16 w-16 mx-auto text-slate-300 mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">
              Your cart is empty
            </h2>
            <p className="text-slate-600 mb-6">
              Start shopping to add items to your cart
            </p>
            <Button onClick={() => navigate("/products")} data-testid="browse-products-button">
              Browse Products
            </Button>
          </div>
        )}
      </div>
    </Layout>
  );
}
